import { add as addRequest } from 'serviceWorker/db/httpRequests';
import { updateOne as editDocumentation } from 'serviceWorker/db/documentations/documentations';
import { updateOne as editDirectory } from 'serviceWorker/db/directories/directories';
import {
  HttpRequestModelType,
  HttpRequestStatus,
} from 'shared/domain/httpRequest/httpRequestModel';
import { SyncStatus } from 'shared/domain/entitySyncStatus/syncStatus';
import { Identificable } from 'shared/types/commonView';
import { get as getCurrentProject } from 'serviceWorker/db/selectedProject';
import { debugLog } from 'shared/logger/debugLog';
import { broadcastUpdatedDocumentation } from 'serviceWorker/broadcasts/documentations';
import { broadcastDirectoryRestored } from 'serviceWorker/broadcasts/directories';
import {
  collectDeletedDirectoryContents,
  DirectoryContents,
} from './collectDirectoryContents';
import {
  DocumentationOperationType,
} from 'shared/domain/documentation/operationPreflight';
import { calculateDocumentationOperationPreflight } from './preflightDocumentationOperation';

class DocumentationRestorer {
  constructor(
    private editDocumentation: (
      documentationLocalId: number,
      changes: { deleted: 0; syncStatus: SyncStatus }
    ) => Promise<unknown>,
    private editDirectory: (
      directoryLocalId: number,
      changes: { deleted: 0; syncStatus: SyncStatus }
    ) => Promise<unknown>,
    private addRequest: (request: {
      createdAt: number;
      method: 'GET';
      data: {
        documentations: number[];
        directories: number[];
        projectId?: string;
      };
      entityType: HttpRequestModelType;
      status: HttpRequestStatus;
    }) => Promise<unknown>,
    private getCurrentProject: () => Promise<Identificable | undefined>,
    private broadcastUpdatedDocumentation: (payload: {
      documentations: number[];
      directories: number[];
    }) => void,
    private collectContents: (
      directoryLocalIds: number[]
    ) => Promise<DirectoryContents>
  ) {}

  async execute(
    documentations: number[],
    directories: number[],
    signature?: string
  ): Promise<void> {
    let allDocumentations: number[];
    let allDirectories: number[];

    if (signature) {
      const preflight = await calculateDocumentationOperationPreflight({
        operationType: DocumentationOperationType.restore,
        documentations,
        directories,
      });

      if (preflight.signature !== signature) {
        throw new Error('PRECONDITION_FAILED');
      }

      allDocumentations = preflight.resolvedDocumentations;
      allDirectories = preflight.resolvedDirectories;
    } else {
      const nestedContents = await this.collectContents(directories);

      allDocumentations = [
        ...documentations,
        ...nestedContents.documentationLocalIds,
      ];
      allDirectories = [
        ...directories,
        ...nestedContents.directoryLocalIds,
      ];
    }

    await Promise.all([
      ...allDocumentations.map((docLocalId) =>
        this.editDocumentation(docLocalId, {
          deleted: 0,
          syncStatus: SyncStatus.PENDING,
        })
      ),
      ...allDirectories.map((dirLocalId) =>
        this.editDirectory(dirLocalId, {
          deleted: 0,
          syncStatus: SyncStatus.PENDING,
        })
      ),
    ]);

    this.broadcastUpdatedDocumentation({
      documentations: allDocumentations,
      directories: allDirectories,
    });
    if (allDirectories.length > 0) {
      broadcastDirectoryRestored(allDirectories);
    }

    const currentProject = await this.getCurrentProject();
    await this.addRequest({
      createdAt: Date.now(),
      method: 'GET',
      data: {
        documentations,
        directories,
        projectId: currentProject?._id,
      },
      entityType: HttpRequestModelType.documentationRestore,
      status: HttpRequestStatus.NEW,
    });
  }
}

export const documentationRestorer = new DocumentationRestorer(
  (documentationLocalId, changes) =>
    editDocumentation(documentationLocalId, changes),
  (directoryLocalId, changes) => editDirectory(directoryLocalId, changes),
  addRequest,
  getCurrentProject,
  (payload: { documentations: number[]; directories: number[] }) => {
    debugLog('broadcastUpdatedDocumentation (restore)', payload);
    broadcastUpdatedDocumentation(payload);
  },
  collectDeletedDirectoryContents
);
